import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  FormField,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import { Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import type { RetryPendingResult } from './services/e-invoice-account.service';

// Provider options are loaded dynamically from EInvoiceProviderService

// Status options
export const ACCOUNT_STATUS_OPTIONS = [
  { label: 'e_invoice.account.status.active', value: true },
  { label: 'e_invoice.account.status.inactive', value: false },
];

// Template health filter values (applied to the loaded page; see component)
export const TEMPLATE_HEALTH = {
  OK: 'ok',
  EXHAUSTED: 'exhausted',
} as const;

export type TemplateHealth =
  (typeof TEMPLATE_HEALTH)[keyof typeof TEMPLATE_HEALTH];

export const TEMPLATE_HEALTH_OPTIONS = [
  { label: 'e_invoice.account.health.ok', value: TEMPLATE_HEALTH.OK },
  {
    label: 'e_invoice.account.health.exhausted',
    value: TEMPLATE_HEALTH.EXHAUSTED,
  },
];

// Search form config
export const ACCOUNT_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'providerId',
    label: 'e_invoice.account.search.provider',
    type: 'select',
    options: [],
    placeholder: 'e_invoice.account.search.select_provider',
    showClear: true,
  },
  {
    key: 'status',
    label: 'e_invoice.account.search.status',
    type: 'select',
    options: ACCOUNT_STATUS_OPTIONS,
    placeholder: 'e_invoice.account.search.select_status',
    showClear: true,
  },
  {
    key: 'health',
    label: 'e_invoice.account.search.health',
    type: 'select',
    options: TEMPLATE_HEALTH_OPTIONS,
    placeholder: 'e_invoice.account.search.select_health',
    showClear: true,
  },
];

// Table columns config: the everyday columns are shown by default; the
// usage/audit columns are opt-in through the column selector.
export const ACCOUNT_COLUMNS: TableColumn[] = [
  {
    label: 'e_invoice.account.table.company_name',
    prop: 'companyName',
    width: 180,
    align: 'left',
    type: 'custom',
  },
  {
    label: 'e_invoice.account.table.provider_name',
    prop: 'providerName',
    width: 130,
    align: 'left',
  },
  {
    label: 'e_invoice.account.table.username',
    prop: 'username',
    width: 130,
    align: 'left',
  },
  {
    label: 'e_invoice.account.table.template_series',
    prop: 'templateSeries',
    width: 200,
    align: 'left',
    type: 'custom',
  },
  {
    label: 'e_invoice.account.table.pending_orders',
    prop: 'pendingFailedCount',
    width: 150,
    align: 'center',
    type: 'custom',
  },
  {
    label: 'e_invoice.account.table.status',
    prop: 'status',
    width: 170,
    align: 'center',
    type: 'custom',
  },
  {
    label: 'e_invoice.account.table.operations',
    prop: 'actions',
    width: 120,
    align: 'center',
    type: 'actions',
  },
];

export const ACCOUNT_FLEXIBLE_COLUMNS: TableColumn[] = [
  {
    label: 'e_invoice.account.table.issued_count',
    prop: 'issuedCount',
    width: 120,
    align: 'right',
    type: 'number',
  },
  {
    label: 'e_invoice.account.table.exhausted_at',
    prop: 'exhaustedAt',
    width: 170,
    align: 'left',
    type: 'custom',
  },
  {
    label: 'e_invoice.account.table.last_issued_at',
    prop: 'lastIssuedAt',
    width: 170,
    align: 'left',
    type: 'custom',
  },
];

// Form config
export type AccountFormMode = 'add' | 'edit' | 'view';

export const ACCOUNT_LIST_ROUTE = '/configuration/e-invoice/account';

export interface AccountFormConfig {
  title: string;
  formFields: FormField[];
  headerActions: ActionButton[];
  listRoute: string;
}

export const ACCOUNT_FORM_CONFIG: AccountFormConfig = {
  title: 'e_invoice.account.form.title_add',
  formFields: [
    {
      label: 'e_invoice.account.form.fields.company',
      controlName: 'companyId',
      type: 'searchableDropdown',
      placeholder: 'e_invoice.account.form.placeholders.select_company',
      required: true,
      section: 'e_invoice.account.form.sections.basic_info',
      sectionGridCols: 2,
      options: [],
      showClear: true,
      validators: [Validators.required],
      errorMessages: {
        required: 'e_invoice.account.messages.error.company_required',
        companyHasAccount:
          'e_invoice.account.messages.error.company_active_duplicate',
      },
    },
    {
      label: 'e_invoice.account.form.fields.provider',
      controlName: 'providerId',
      type: 'dropdown',
      placeholder: 'e_invoice.account.form.placeholders.select_provider',
      required: true,
      section: 'e_invoice.account.form.sections.basic_info',
      options: [],
      validators: [Validators.required],
      errorMessages: {
        required: 'e_invoice.account.messages.error.provider_required',
      },
    },
    {
      label: 'e_invoice.account.form.fields.status',
      controlName: 'active',
      type: 'checkbox',
      placeholder: 'e_invoice.account.form.placeholders.active_hint',
      section: 'e_invoice.account.form.sections.basic_info',
      defaultValue: true,
      validators: [],
      errorMessages: {},
    },
    {
      label: 'e_invoice.account.form.fields.username',
      controlName: 'username',
      type: 'text',
      placeholder: 'e_invoice.account.form.placeholders.enter_username',
      required: true,
      section: 'e_invoice.account.form.sections.account',
      sectionGridCols: 2,
      validators: [Validators.required],
      errorMessages: {
        required: 'e_invoice.account.messages.error.username_required',
      },
    },
    {
      label: 'e_invoice.account.form.fields.password',
      controlName: 'password',
      type: 'password',
      placeholder: 'e_invoice.account.form.placeholders.enter_password',
      required: true,
      section: 'e_invoice.account.form.sections.account',
      validators: [Validators.required],
      errorMessages: {
        required: 'e_invoice.account.messages.error.password_required',
      },
    },
    {
      label: 'e_invoice.account.form.fields.invoice_template',
      controlName: 'invoiceTemplate',
      type: 'searchableDropdown',
      placeholder: 'e_invoice.account.form.placeholders.select_invoice_template',
      required: true,
      section: 'e_invoice.account.form.sections.settings',
      sectionGridCols: 2,
      options: [],
      showClear: true,
      disabled: true,
      validators: [Validators.required],
      errorMessages: {
        required: 'e_invoice.account.messages.error.template_required',
      },
    },
    {
      label: 'e_invoice.account.form.fields.invoice_series',
      controlName: 'invoiceSeries',
      type: 'searchableDropdown',
      placeholder: 'e_invoice.account.form.placeholders.select_invoice_series',
      required: true,
      section: 'e_invoice.account.form.sections.settings',
      options: [],
      showClear: true,
      disabled: true,
      validators: [Validators.required],
      errorMessages: {
        required: 'e_invoice.account.messages.error.series_required',
      },
    },
  ],
  headerActions: [
    {
      label: 'common.actions.cancel',
      action: 'cancel',
      severity: 'secondary',
      outlined: true,
    },
    {
      label: 'common.actions.save',
      action: 'save',
      severity: 'info',
    },
  ],
  listRoute: ACCOUNT_LIST_ROUTE,
};

// Status helpers
export function getStatusSeverity(
  status: boolean,
): 'success' | 'warn' | 'danger' | 'secondary' {
  return status ? 'success' : 'secondary';
}

export function getStatusDisplayText(
  status: boolean,
  translateService: TranslateService,
): string {
  return status
    ? translateService.instant('e_invoice.account.status.active')
    : translateService.instant('e_invoice.account.status.inactive');
}

export interface RetryToast {
  severity: 'info' | 'warn' | 'success';
  summaryKey: string;
  detailKey: string;
}

/**
 * Toast for a replay result. A replay handles one batch, and another replay may
 * already hold the account, so the counts are only trusted when this call ran.
 */
export function getRetryToast(result: RetryPendingResult): RetryToast {
  if (result.alreadyRunning) {
    return {
      severity: 'info',
      summaryKey: 'common.messages.info',
      detailKey: 'e_invoice.account.messages.retry.already_running',
    };
  }
  if (result.quotaExhausted) {
    return {
      severity: 'warn',
      summaryKey: 'common.messages.warning',
      detailKey: 'e_invoice.account.messages.retry.still_exhausted',
    };
  }
  return {
    severity: 'success',
    summaryKey: 'common.messages.success',
    detailKey:
      (result.remainingPending ?? 0) > 0
        ? 'e_invoice.account.messages.retry.done_more'
        : 'e_invoice.account.messages.retry.done',
  };
}
